import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { components } from "@/types/api";

export type MailboxConnection =
  components["schemas"]["MailboxConnectionResponse"];
export type MailboxConnectionCreate =
  components["schemas"]["MailboxConnectionCreate"];
export type MailboxConnectionUpdate =
  components["schemas"]["MailboxConnectionUpdate"];
export type TestConnectionResult =
  components["schemas"]["TestConnectionResult"];
export type InboundEmailListItem =
  components["schemas"]["InboundEmailListItem"];
export type InboundEmailResponse =
  components["schemas"]["InboundEmailResponse"];
export type TimelineItem = components["schemas"]["TimelineItem"];
export type InboxEntityType = "contact" | "lead" | "contract";

export const inboxKeys = {
  connection: ["inbox", "connection"] as const,
  messages: (status?: string) => ["inbox", "messages", status ?? "all"] as const,
  message: (id: string) => ["inbox", "message", id] as const,
  timeline: (entityType: string, entityId: string) =>
    ["inbox", "timeline", entityType, entityId] as const,
};

/** The connected mailbox, or null when none is configured (404). */
export function useMailboxConnection() {
  return useQuery({
    queryKey: inboxKeys.connection,
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET(
        "/api/v1/inbox/connection",
        {},
      );
      if (response.status === 404) return null;
      if (error || !data) throw new Error("Failed to load the mailbox connection");
      return data;
    },
  });
}

export function useSaveMailboxConnection(hasConnection: boolean) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: MailboxConnectionCreate | MailboxConnectionUpdate,
    ) => {
      const call = hasConnection
        ? apiClient.PATCH("/api/v1/inbox/connection", {
            body: body as MailboxConnectionUpdate,
          })
        : apiClient.POST("/api/v1/inbox/connection", {
            body: body as MailboxConnectionCreate,
          });
      const { data, error } = await call;
      if (error || !data) throw new Error("Could not save the mailbox connection");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: inboxKeys.connection }),
  });
}

export function useDeleteMailboxConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.DELETE("/api/v1/inbox/connection", {});
      if (error) throw new Error("Could not disconnect the mailbox");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inboxKeys.connection });
      qc.invalidateQueries({ queryKey: ["inbox", "messages"] });
    },
  });
}

/** Test credentials before saving (ad-hoc) or the already-saved connection. */
export function useTestMailbox() {
  return useMutation({
    mutationFn: async (
      payload: MailboxConnectionCreate | "saved",
    ): Promise<TestConnectionResult> => {
      const call =
        payload === "saved"
          ? apiClient.POST("/api/v1/inbox/connection/test", {})
          : apiClient.POST("/api/v1/inbox/test", { body: payload });
      const { data, error } = await call;
      if (error || !data) throw new Error("Could not reach the mail server");
      return data;
    },
  });
}

export function useInboxMessages(status?: string) {
  return useQuery({
    queryKey: inboxKeys.messages(status),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/inbox/messages", {
        params: { query: status ? { status } : {} },
      });
      if (error || !data) throw new Error("Failed to load messages");
      return data;
    },
  });
}

export function useInboxMessage(id: string | undefined) {
  return useQuery({
    queryKey: inboxKeys.message(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/inbox/messages/{message_id}",
        { params: { path: { message_id: id! } } },
      );
      if (error || !data) throw new Error("Message not found");
      return data;
    },
  });
}

export function useLinkMessage(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { entity_type: InboxEntityType; entity_id: string }) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/inbox/messages/{message_id}/links",
        { params: { path: { message_id: id } }, body: input },
      );
      if (error || !data) throw new Error("Could not link this message");
      return data;
    },
    onSuccess: () => invalidateMessage(qc, id),
  });
}

export function useUnlinkMessage(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (linkId: string) => {
      const { data, error } = await apiClient.DELETE(
        "/api/v1/inbox/messages/{message_id}/links/{link_id}",
        { params: { path: { message_id: id, link_id: linkId } } },
      );
      if (error || !data) throw new Error("Could not remove this link");
      return data;
    },
    onSuccess: () => invalidateMessage(qc, id),
  });
}

export function useRematchMessage(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST(
        "/api/v1/inbox/messages/{message_id}/rematch",
        { params: { path: { message_id: id } } },
      );
      if (error || !data) throw new Error("Could not re-run matching");
      return data;
    },
    onSuccess: () => invalidateMessage(qc, id),
  });
}

export function useCorrespondence(
  entityType: InboxEntityType,
  entityId: string | undefined,
) {
  return useQuery({
    queryKey: inboxKeys.timeline(entityType, entityId ?? ""),
    enabled: Boolean(entityId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/inbox/timeline", {
        params: { query: { entity_type: entityType, entity_id: entityId! } },
      });
      if (error || !data) throw new Error("Failed to load correspondence");
      return data;
    },
  });
}

function invalidateMessage(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
) {
  qc.invalidateQueries({ queryKey: inboxKeys.message(id) });
  qc.invalidateQueries({ queryKey: ["inbox", "messages"] });
  qc.invalidateQueries({ queryKey: ["inbox", "timeline"] });
}
